import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box, Typography, TextField, Button, CircularProgress, Chip,
  Grid, Card, CardMedia, CardContent, Checkbox,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LockIcon from "@mui/icons-material/Lock";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT || "";

interface ShoplineStatus {
  connected: boolean;
  store_handle?: string;
  shop_name?: string;
}

interface ShoplineProduct {
  id: string;
  title: string;
  images?: { src: string }[];
  variants?: { price: string }[];
}

// Accepts "mystore", "mystore.myshopline.com", or "https://mystore.myshopline.com/..."
// and always reduces it to the bare handle the API expects.
const extractStoreHandle = (input: string): string => {
  let h = input.trim();
  h = h.replace(/^https?:\/\//i, "");
  h = h.split("/")[0];
  h = h.replace(/\.myshopline\.com.*$/i, "");
  h = h.replace(/^www\./i, "");
  return h;
};

const ConnectShopline: React.FC = () => {
  const [status, setStatus] = useState<ShoplineStatus>({ connected: false });
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [storeHandle, setStoreHandle] = useState("");
  const [connecting, setConnecting] = useState(false);

  // --- product list state ---
  const [products, setProducts] = useState<ShoplineProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [nextPageInfo, setNextPageInfo] = useState<string | null>(null);

  // --- selection / ingest state ---
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [ingesting, setIngesting] = useState(false);

  const getEmail = () => localStorage.getItem("user_email") || "";

  const fetchStatus = useCallback(async () => {
    const email = getEmail();
    if (!email) {
      setStatusLoaded(true);
      return;
    }
    try {
      const body = new URLSearchParams({ email });
      const { data } = await axios.post(
        `${BASE_URL}/api/integrations/shopline/status`,
        body,
        { withCredentials: true }
        // axios sets Content-Type: application/x-www-form-urlencoded automatically for URLSearchParams
      );
      setStatus(data);
    } catch (err) {
      console.error("Failed to fetch Shopline status", err);
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  const fetchProducts = useCallback(async (pageInfo?: string | null) => {
    const email = getEmail();
    if (!email) return;
    setProductsLoading(true);
    try {
      const body = new URLSearchParams({ email });
      if (pageInfo) body.set("page_info", pageInfo);
      const { data } = await axios.post(
        `${BASE_URL}/api/integrations/shopline/products`,
        body,
        { withCredentials: true }
      );
      setProducts((prev) => (pageInfo ? [...prev, ...(data.products || [])] : data.products || []));
      setNextPageInfo(data.next_page_info || null);
    } catch (err) {
      console.error("Failed to fetch Shopline products", err);
      toast.error("Could not load products.");
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Load products once we know the store is connected.
  useEffect(() => {
    if (status.connected) fetchProducts();
  }, [status.connected, fetchProducts]);

  // Detect return from OAuth (same convention as your social platforms)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("shopline") === "connected") {
      toast.success("Shopline store connected!");
      params.delete("shopline");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
      fetchStatus();
    } else if (params.get("shopline") === "error") {
      const reason = params.get("reason");
      toast.error(reason ? `Shopline connection failed: ${reason}` : "Shopline connection failed.");
      params.delete("shopline");
      params.delete("reason");
      const newSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }
  }, [fetchStatus]);

  const handleConnect = async () => {
    const handle = extractStoreHandle(storeHandle);
    const email = getEmail();

    if (!handle) {
      toast.error("Enter your Shopline store handle first.");
      return;
    }
    if (!email) {
      toast.error("You need to be logged in to connect Shopline.");
      return;
    }

    setConnecting(true);
    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/integrations/shopline/auth/start`,
        { store_handle: handle, email }, // JSON body — matches your curl example
        { withCredentials: true }
      );
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("Could not start Shopline authorization.");
        setConnecting(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Connection failed");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Shopline store?")) return;
    const email = getEmail();
    try {
      const body = new URLSearchParams({ email });
      const { data } = await axios.post(
        `${BASE_URL}/api/integrations/shopline/disconnect`,
        body,
        { withCredentials: true }
      );
      toast.success(typeof data === "string" && data ? data : "Shopline disconnected.");
      setProducts([]);
      setNextPageInfo(null);
      setSelectedIds(new Set());
      fetchStatus();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === products.length ? new Set() : new Set(products.map((p) => p.id))
    );
  };

  const handleIngest = async () => {
    const email = getEmail();
    if (selectedIds.size === 0) {
      toast.error("Select at least one product first.");
      return;
    }
    setIngesting(true);
    try {
      const body = new URLSearchParams();
      body.set("email", email);
      body.set("product_ids", Array.from(selectedIds).join(","));
      const { data } = await axios.post(
        `${BASE_URL}/api/integrations/shopline/products/ingest`,
        body,
        { withCredentials: true }
      );
      toast.success(`${data.ingested_count ?? selectedIds.size} product(s) added to your content library.`);
      setSelectedIds(new Set());
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to add products to content library.");
    } finally {
      setIngesting(false);
    }
  };

  if (!statusLoaded) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Connect Shopline
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 3 }}>
        Connect your Shopline store to bring your product catalog in as content for posts and blogs.
      </Typography>

      {status.connected ? (
        <>
          <Box sx={{
            p: 3, borderRadius: 2, border: "1px solid rgba(34,197,94,0.4)",
            background: "rgba(34,197,94,0.08)", mb: 3,
          }}>
            <Chip label="Connected" color="success" size="small" sx={{ mb: 1 }} />
            <Typography sx={{ fontWeight: 600 }}>
              {status.shop_name || status.store_handle}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}>
              {status.store_handle}.myshopline.com
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={handleDisconnect}>
              Disconnect
            </Button>
          </Box>

          {/* --- Product list --- */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Products {products.length > 0 && `(${products.length})`}
              </Typography>
              {products.length > 0 && (
                <Button size="small" onClick={toggleSelectAll}>
                  {selectedIds.size === products.length ? "Deselect all" : "Select all"}
                </Button>
              )}
            </Box>
            {products.length > 0 && (
              <Button
                variant="contained"
                size="small"
                disabled={selectedIds.size === 0 || ingesting}
                onClick={handleIngest}
                startIcon={ingesting ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {ingesting ? "Adding…" : `Add ${selectedIds.size > 0 ? selectedIds.size : ""} to Content Library`}
              </Button>
            )}
          </Box>

          {productsLoading && products.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : products.length === 0 ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
              No products found in this store yet.
            </Typography>
          ) : (
            <>
              <Grid container spacing={2}>
                {products.map((p) => {
                  const image = p.images?.[0]?.src;
                  const price = p.variants?.[0]?.price;
                  const isSelected = selectedIds.has(p.id);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                      <Card
                        onClick={() => toggleSelect(p.id)}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          background: "rgba(255,255,255,0.03)",
                          border: isSelected ? "1px solid #6366f1" : "1px solid rgba(255,255,255,0.08)",
                          outline: isSelected ? "1px solid #6366f1" : "none",
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(p.id)}
                          sx={{
                            position: "absolute", top: 4, left: 4, zIndex: 1,
                            color: "rgba(255,255,255,0.7)",
                            background: "rgba(0,0,0,0.4)", borderRadius: 1,
                            "&.Mui-checked": { color: "#6366f1" },
                          }}
                          size="small"
                        />
                        {image ? (
                          <CardMedia component="img" height="140" image={image} alt={p.title} sx={{ objectFit: "cover" }} />
                        ) : (
                          <Box sx={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.05)" }}>
                            <ImageNotSupportedIcon sx={{ color: "rgba(255,255,255,0.2)" }} />
                          </Box>
                        )}
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{p.title}</Typography>
                          {price && (
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>${price}</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {nextPageInfo && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fetchProducts(nextPageInfo)}
                    disabled={productsLoading}
                    startIcon={productsLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {productsLoading ? "Loading…" : "Load more"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      ) : (
        <Box sx={{
          p: 3, borderRadius: 2, border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.03)", mb: 3,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <StorefrontIcon sx={{ color: "#6366f1" }} />
            <Typography sx={{ fontWeight: 600 }}>Shopline</Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="Store handle"
            placeholder="mystore"
            helperText="From your store URL — mystore.myshopline.com"
            value={storeHandle}
            onChange={(e) => setStoreHandle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connecting}
            startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {connecting ? "Connecting…" : "Connect with Shopline"}
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <LockIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
              You'll be redirected to Shopline to securely authorize access to your product catalog.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ConnectShopline;